import React, { useCallback } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";

import { WACategoryScreenProps } from "../WANavigation";
import { WACategoryWidgetContainer } from "../WACategoryWidgetContainer";

import { useWACategoryLayout } from "../utils/useWACategoryLayout";
import { ScrollView } from "react-native";

import { WACategoryOrgQuery } from "./__generated__/WACategoryOrgQuery.graphql";

import { WACategoryOrgSpaceMutation } from "./__generated__/WACategoryOrgSpaceMutation.graphql";
import { WidgetSpace } from "widgets/src/WidgetSpace/WidgetSpace";
import { useWidgetMargin } from "../../Widget/useWidgetMargin";
import { WACategoryItemSeparator } from "../WACategoryItemSeparator";

type Props = WACategoryScreenProps<"WACategoryOrgInfo">;

export function WACategoryOrg(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerWACategoryOrg {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWACategoryOrg(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WACategoryOrgQuery>(
    graphql`
      query WACategoryOrgQuery($storecastId: ID!) {
        dashboard(id: $storecastId) @required(action: THROW) {
          id
          previewWidgetSpaces {
            id
            height
            spaceId
            spaceName
            ...WidgetSpace_widgetSpace
          }
        }
      }
    `,
    {
      storecastId: props.route.params.storecastId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );
  const intl_tokens = useIntlTokens();

  const contentContainerStyle = useWACategoryLayout();
  const [createWidgetNote] = useMutation<WACategoryOrgSpaceMutation>(graphql`
    mutation WACategoryOrgSpaceMutation(
      $storecastId: ID!
      $widget: CreateWidgetSpaceInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        createWidgetSpace(widget: $widget) {
          id
        }
      }
    }
  `);

  const onCreate = useCallback(
    (spaceId: string) => {
      createWidgetNote({
        variables: {
          storecastId: props.route.params.storecastId,
          widget: {
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            spaceId,
          },
        },
        onCompleted: () => {
          props.navigation.goBack();
          props.navigation.pop(1);
        },
      });
    },
    [
      createWidgetNote,
      props.route.params.storecastId,
      props.route.params.position.x,
      props.route.params.position.c,
      props.route.params.position.y,
      props.navigation,
    ]
  );

  const margin = useWidgetMargin();

  return (
    <ScrollView contentContainerStyle={contentContainerStyle}>
      {data.dashboard.previewWidgetSpaces.map((widget) => {
        return (
          <React.Fragment key={widget.id}>
            <WACategoryWidgetContainer
              hasSizeWithinBounds={
                props.route.params.maxSize.h >= widget.height &&
                props.route.params.maxSize.w >= 2
              }
              title={widget.spaceName ?? ""}
              size={{
                h: widget.height as 1 | 2 | 3 | 4,
                w: 2,
              }}
              overflow="visible"
              unitSize={props.route.params.unitSize}
              onAdd={() => onCreate(widget.spaceId)}
              addTitle={intl_tokens.Add}
            >
              <WidgetSpace
                mode="IDLE"
                widget={widget}
                interactive={false}
                margin={margin}
              />
            </WACategoryWidgetContainer>
            <WACategoryItemSeparator />
          </React.Fragment>
        );
      })}
    </ScrollView>
  );
}
